import React from "react";
import { FiCalendar } from "react-icons/fi";

import { ReactComponent as ChevronRightIcon } from "../../assets/icons/chevron-right.svg";
import Select, { SelectOption } from "./Select";

interface SteppedSelectProps {
  options: SelectOption[]; // newest first
  index: number;
  onChange: (index: number) => void;
  /** Older = a step to a higher index; pass to keep it enabled at the end of a list that can load more. */
  canStepOlder?: boolean;
  /** Replaces the default step-older, e.g. to fetch the next page first. */
  onStepOlder?: () => void;
  icon?: React.ReactNode;
  olderLabel?: string;
  newerLabel?: string;
  className?: string;
  size?: "sm" | "md";
  /** Trigger text while nothing is picked (index -1); it can't be picked back. */
  placeholder?: string;
}

// Toolbar icon button squared off for the joined control; disabled keeps pointer events so the end of the list shows not-allowed
const STEPPER =
  "eai-btn-toolbar-icon shrink-0 !rounded-none disabled:pointer-events-auto disabled:cursor-not-allowed disabled:hover:border-eai-border disabled:hover:text-eai-ink-muted disabled:active:bg-eai-surface-control";

/** ◀ [picker] ▶ as one segmented control — step through a newest-first list (forecast issues, data files, model results). */
const SteppedSelect: React.FC<SteppedSelectProps> = ({
  options,
  index,
  onChange,
  canStepOlder,
  onStepOlder,
  icon = <FiCalendar aria-hidden className="h-3.5 w-3.5" />,
  olderLabel = "Older",
  newerLabel = "Newer",
  className = "",
  size = "sm",
  placeholder,
}) => {
  // Nothing picked yet (-1): there's no position to step from
  const hasOlder = canStepOlder ?? (index >= 0 && index < options.length - 1);
  const stepper = `${STEPPER} ${size === "md" ? "!h-eai-control-lg !w-eai-control-lg" : ""}`;
  return (
    <div className={`flex w-full items-center ${className}`}>
      <button
        type="button"
        aria-label={olderLabel}
        onClick={() => (onStepOlder ? onStepOlder() : onChange(index + 1))}
        disabled={!hasOlder}
        className={`${stepper} !rounded-l-md`}
      >
        <ChevronRightIcon className="h-4 w-4 rotate-180" />
      </button>
      <Select
        containerClassName="min-w-0 flex-1"
        className="!rounded-none border-x-0"
        size={size}
        icon={icon}
        options={options}
        placeholder={placeholder}
        value={index}
        // The placeholder row has value "", which isn't an index
        onChange={(e) => e.target.value !== "" && onChange(Number(e.target.value))}
      />
      <button
        type="button"
        aria-label={newerLabel}
        onClick={() => onChange(index - 1)}
        disabled={index <= 0}
        className={`${stepper} !rounded-r-md`}
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default SteppedSelect;
